// UpdateGate — how a crew actually ends up on the new build.
//
// Extracted verbatim in behaviour from the classic Layout (deleted with the
// cutover, docs/ONE_APP.md) because it is the mechanism behind the owner's
// actual requirement: "make sure everyone can get the latest upgraded app when
// opening the app." Publishing a build does nothing on its own — a phone that
// has the app open, or a home-screen PWA, keeps running the service worker it
// already has until something tells it not to.
//
// Flow, and each step exists for a reason learned the hard way:
//
//   1. Watch the registration's `updatefound` — fires when a new SW installs.
//   2. When it reaches `installed` AND there is already a controller, a new
//      version is waiting. First install does not count: there is nothing to
//      update FROM, and a toast then would ask a person to refresh the page
//      they just opened.
//   3. "Update now" posts SKIP_WAITING; `controllerchange` fires; reload.
//      The reload is armed by that tap and by nothing else — see
//      `askedRef`. A controllerchange the app did not ask for is a first
//      install or a browser-initiated swap, and restarting a crew member's
//      page for either is the 2026-09-18 report.
//   4. Dismiss just hides it. The next reload or deploy surfaces it again —
//      it is a nudge, not a lock, because a crew member mid-clock-in must not
//      be forced to reload.
//   5. It says WHAT CHANGED, from /changelog.json, written into the build at
//      deploy time (.github/workflows/deploy.yml). Empty or missing means show
//      no list at all: a "what's new" that says "internal improvements" every
//      day trains a crew to dismiss it, and then they dismiss the day it
//      matters. A worker who knows Timesheets changed gives a far better
//      report than one who only knows "it's broken".
//
// It also re-checks ON FOCUS. The browser's own check is roughly daily, and a
// phone left open in a truck all day would otherwise never notice a deploy.
// That is the difference between "we deployed" and "they are running it".

import React, { useEffect, useRef, useState } from 'react';
import { Notice, FwButton } from '../../ui/fw';
import './UpdateGate.css';

/**
 * The manual escape hatch: clear every cached build, unregister the worker,
 * hard reload. Exported because the Me screen offers it — it is what a crew is
 * told to press when the app is behaving oddly, so it has to work on the
 * machine where the automatic path already did not.
 *
 * localStorage is deliberately NOT touched. It holds no cache here — the
 * caches are the Cache API and the service worker, both cleared above — and
 * clearing it destroyed every preference on every update: theme mode back to
 * the phone's default (reported by a worker), the customised bottom bar, the
 * dismissed install prompt. noLocalStorageClear.test.js keeps it that way.
 */
export async function forceUpdate() {
  try {
    if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
      const reg = await navigator.serviceWorker.getRegistration();
      // BOTH halves matter. With no registration the `&&` is what stops a
      // null dereference that the catch would swallow — and the reload would
      // then happen with every stale asset still in the Cache API, which is
      // precisely the state a crew presses this button in.
      if (reg && reg.active) reg.active.postMessage('CLEAR_ALL_CACHES');
      const all = await navigator.serviceWorker.getRegistrations();
      for (const r of all || []) await r.unregister();
    }
    if (typeof window !== 'undefined' && 'caches' in window) {
      const keys = await window.caches.keys();
      await Promise.all(keys.map((k) => window.caches.delete(k)));
    }
  } catch (_) {
    // Fall through to the reload either way; a failure here must not leave a
    // person on a screen with a button that did nothing.
  }
  window.location.reload(true);
}

export default function UpdateGate() {
  // Did WE ask for the swap? A ref and not state: the controllerchange
  // listener is registered once, in an effect with no dependencies, so it
  // closes over the first render's values and would never see a state update.
  const askedRef = useRef(false);
  // Shared by the controllerchange handler and apply()'s watchdog, so the two
  // cannot both reload. A ref for the same reason askedRef is one: the
  // listener is registered in a `[]`-dep effect and never sees a state update.
  const reloadedRef = useRef(false);
  // The watchdog's timer id, so unmount can cancel it. AppShell renders a
  // phone branch and a desktop branch, and crossing the breakpoint remounts
  // this component — without the clear, a timer armed before the resize would
  // reload a page whose user never pressed anything. Raised in review.
  const watchdogRef = useRef(null);
  const [available, setAvailable] = useState(false);
  const [registration, setRegistration] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [notes, setNotes] = useState([]);

  useEffect(() => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return undefined;
    let cancelled = false;

    // Which registration we have already attached listeners to. `watch` is
    // called from more than one place now (see below), and attaching
    // `updatefound` twice would raise the prompt twice for one build.
    let watched = null;

    const watch = (reg) => {
      if (!reg || cancelled || reg === watched) return;
      watched = reg;
      // Already waiting on first load — surface it immediately.
      if (reg.waiting && navigator.serviceWorker.controller) {
        setRegistration(reg);
        setAvailable(true);
      }
      reg.addEventListener('updatefound', () => {
        const next = reg.installing;
        if (!next) return;
        next.addEventListener('statechange', () => {
          if (next.state === 'installed' && navigator.serviceWorker.controller) {
            setRegistration(reg);
            setAvailable(true);
            // A NEW build is a new question. `dismissed` had no other writer
            // and was never reset, which was harmless only while the service
            // worker skipped waiting: "Later" cost nothing because the build
            // swapped anyway. Now that the choice is real, a permanent flag
            // means one tap at 7am leaves somebody on that morning's build
            // through every deploy for the rest of the day.
            //
            // The focus re-check cannot cover this: reg.update() on a script
            // already in `waiting` fetches identical bytes and aborts without
            // firing updatefound, so only a genuinely newer build gets here.
            // That is also why this does not re-nag — `updatefound` fires once
            // per installing worker and `installed` is one transition.
            setDismissed(false);
          }
        });
      });
    };

    navigator.serviceWorker.getRegistration().then(watch).catch(() => {});

    // ── And again once the browser HAS one ────────────────────────────────
    //
    // client/public/index.html defers `register()` to `window.load`. If this
    // component ever mounts before that, the call above resolves `undefined`,
    // `watch` returns, and no `updatefound` listener exists for the life of
    // the page — so a new build parks in `waiting` and nobody is told, now
    // that the worker no longer skips waiting.
    //
    // MEASURED against the real built app, 2026-09-18, and it did NOT
    // reproduce: `load` fired at 122ms and the call above resolved a real
    // registration at 208ms, because this component mounts behind the auth
    // gate. `ready` is belt-and-braces for an ordering nothing guarantees —
    // it resolves once there is an active worker, whenever that is, and
    // `watched` makes the double call free. Raised in review.
    if (navigator.serviceWorker.ready && typeof navigator.serviceWorker.ready.then === 'function') {
      navigator.serviceWorker.ready.then(watch).catch(() => {});
    }

    // After SKIP_WAITING the new worker takes control; reload so the page
    // matches it. The flag guards a double reload.
    //
    // ── ONLY when this component asked for it ──────────────────────────────
    //
    // `controllerchange` does not mean "the crew pressed Update now". It means
    // a worker took control, which also happens on a FIRST install (the
    // service worker calls clients.claim()) and on any activation the browser
    // does by itself. Reloading on all of them is a crew member's page
    // restarting under them with no warning — reported by a worker 2026-09-18
    // as having "got an update" they never asked for, and measured in a
    // browser the same day: two controllerchange events across one deploy,
    // nobody having pressed anything.
    //
    // It also made this component's own notice dishonest. It says "Anything
    // you have typed is not saved yet — finish that first", and then the page
    // reloaded before they could, including after they pressed Later.
    //
    // So the reload belongs to the button. `askedRef` is set in apply() and
    // is the only thing that arms this.
    // Was this page already under a worker? If not, the FIRST controllerchange
    // is simply the first install claiming it, and neither reloading nor
    // prompting is right — there is nothing to update FROM.
    //
    // NOT a `const` read once at mount, which is what it was and what review
    // caught. On a first-install page that snapshot is `false` for the rest of
    // the page's life, so a LATER deploy claiming this tab — the stranded tab
    // this prompt exists to rescue — was silently excluded from it. It is the
    // tab most likely to be stranded, because it is the one that has been open
    // longest.
    let hadController = !!navigator.serviceWorker.controller;

    const onControllerChange = () => {
      if (!askedRef.current) {
        // Somebody else's tab pressed Update now. The new worker has activated,
        // deleted every older cache and claimed this tab, and deploy.sh removes
        // the previous build directory — so this page is running JS whose
        // sibling assets are gone from the cache AND from the server.
        //
        // Reloading it is what this handler used to do unconditionally, and it
        // is not safe: somebody may be typing in it. Doing nothing is not safe
        // either. So ask, and let them pick the moment.
        if (hadController) { setAvailable(true); setDismissed(false); }
        // Whatever it was, this page is under a worker from here on, so the
        // NEXT unasked change is a takeover rather than a first install.
        hadController = true;
        return;
      }
      if (reloadedRef.current) return;
      reloadedRef.current = true;
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);

    const onFocus = () => {
      navigator.serviceWorker.getRegistration().then((reg) => {
        if (!reg) return undefined;
        // `watch` first, and it is a no-op for one already watched. This is
        // the other half of the late-registration guard above: if the
        // registration arrived after mount, the update() below would install a
        // worker with nothing listening for it.
        watch(reg);
        return reg.update();
      }).catch(() => {});
    };
    window.addEventListener('focus', onFocus);

    return () => {
      cancelled = true;
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
      window.removeEventListener('focus', onFocus);
      if (watchdogRef.current != null) {
        window.clearTimeout(watchdogRef.current);
        watchdogRef.current = null;
      }
    };
  }, []);

  // What changed, from the NEW build. `cache: 'no-store'` matters — the old
  // service worker would happily serve a cached copy of the old changelog.
  useEffect(() => {
    if (!available) return undefined;
    let cancelled = false;
    fetch('/changelog.json', { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled && d && Array.isArray(d.items)) setNotes(d.items); })
      .catch(() => { /* no notes is fine; never block the update prompt */ });
    return () => { cancelled = true; };
  }, [available]);

  const apply = () => {
    // Set BEFORE the message goes out. The worker can take control between
    // the postMessage and the next line, and a flag set afterwards would
    // arrive too late for the very event it exists to allow.
    askedRef.current = true;
    if (registration && registration.waiting) {
      registration.waiting.postMessage('SKIP_WAITING');
      // ── And reload anyway if that message goes nowhere ──────────────────
      //
      // Before this file stopped the worker skipping waiting, `waiting` was
      // always null here and apply() fell straight through to the reload
      // below — so the button always did something visible. Now it can post a
      // message to a worker that has gone redundant, or that never answers,
      // and the page just sits there with the notice still up.
      //
      // A crew member who taps a button twice and sees nothing stops using it,
      // and this is the button that gets them onto a new build. So: give the
      // handoff its moment, then reload regardless. `controllerchange` fires
      // first in the normal case and sets `reloadedRef`, which makes this a
      // no-op. (That sentence named a local called `reloading` until the flag
      // became a ref shared with the handler — a note that has quietly become
      // false is worse than no note, so it is corrected rather than left.)
      // Raised in review.
      watchdogRef.current = window.setTimeout(() => {
        if (!reloadedRef.current) {
          reloadedRef.current = true;
          window.location.reload();
        }
      }, 3000);
      return; // controllerchange reloads first, normally
    }
    window.location.reload();   // no waiting worker: odd race, just reload
  };

  if (!available || dismissed) return null;
  return (
    <div className="fw-update" role="status">
      <Notice tone="info" title="A new version is ready">
        <div className="fw-update__row">
          <span>Update now to get the latest. Anything you have typed is not saved yet — finish that first.</span>
          {notes.length ? (
            <ul className="fw-update__notes">
              {notes.map((n, i) => <li key={i}>{n}</li>)}
            </ul>
          ) : null}
          <div className="fw-update__actions">
            <FwButton variant="primary" onClick={apply}>Update now</FwButton>
            <FwButton variant="quiet" onClick={() => setDismissed(true)}>Later</FwButton>
          </div>
        </div>
      </Notice>
    </div>
  );
}
